import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Building2, FileText, Phone, Mail, MapPin, Ban as Bank, Globe, DollarSign, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SellerOnboardingDialogProps {
  onClose: () => void;
}

export function SellerOnboardingDialog({ onClose }: SellerOnboardingDialogProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    businessName: '',
    businessType: 'individual' as 'individual' | 'company',
    businessDescription: '',
    taxId: '',
    businessEmail: '',
    businessPhone: '',
    address: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: ''
    },
    website: '',
    socialMedia: {
      facebook: '',
      instagram: '',
      twitter: ''
    },
    categories: [] as string[],
    expectedSales: '',
    sellingExperience: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error: registerError } = await supabase.rpc('register_as_seller', {
        business_name_input: form.businessName,
        business_description_input: form.businessDescription,
        business_type_input: form.businessType,
        tax_id_input: form.taxId,
        contact_info_input: {
          email: form.businessEmail,
          phone: form.businessPhone
        },
        address_input: form.address,
        website_input: form.website,
        social_media_input: form.socialMedia,
        categories_input: form.categories,
        expected_sales_input: form.expectedSales,
        selling_experience_input: form.sellingExperience
      });

      if (registerError) throw registerError;

      // Redirect to seller dashboard
      navigate('/marketplace/seller');
    } catch (error) {
      console.error('Error registering as seller:', error);
      setError(error instanceof Error ? error.message : 'Failed to register as seller');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-dark-paper rounded-xl shadow-xl w-full max-w-2xl animate-scale-in">
        <div className="p-6 border-b dark:border-dark-border">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Become a Seller</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-dark-elevated rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400">
              {error}
            </div>
          )}

          {/* Progress steps */}
          <div className="mb-8">
            <div className="flex items-center justify-between relative">
              <div className="absolute left-0 right-0 top-1/2 h-0.5 bg-gray-200 dark:bg-dark-border" />
              {[1, 2, 3].map((s) => (
                <button
                  key={s}
                  onClick={() => setStep(s)}
                  className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center relative z-10 transition-colors",
                    step === s
                      ? "bg-royal-purple text-white"
                      : step < s
                      ? "bg-white dark:bg-dark-elevated border-2 border-gray-200 dark:border-dark-border text-gray-400 dark:text-gray-500"
                      : "bg-green-500 text-white"
                  )}
                >
                  {step > s ? '✓' : s}
                </button>
              ))}
            </div>
            <div className="flex justify-between mt-2 text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-300">Business Info</span>
              <span className="font-medium text-gray-700 dark:text-gray-300">Contact Details</span>
              <span className="font-medium text-gray-700 dark:text-gray-300">Additional Info</span>
            </div>
          </div>

          {step === 1 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Business Type
                </label>
                <div className="grid gap-4 sm:grid-cols-2">
                  {['individual', 'company'].map((type) => (
                    <label
                      key={type}
                      className={cn(
                        "flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors",
                        form.businessType === type
                          ? "border-royal-purple bg-royal-purple/5 dark:bg-royal-purple/10"
                          : "border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-elevated"
                      )}
                    >
                      <input
                        type="radio"
                        name="businessType"
                        value={type}
                        checked={form.businessType === type}
                        onChange={(e) => setForm(prev => ({
                          ...prev,
                          businessType: e.target.value as 'individual' | 'company'
                        }))}
                        className="w-4 h-4 text-royal-purple focus:ring-royal-purple dark:bg-dark-elevated dark:border-dark-border"
                      />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100 capitalize">{type}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {type === 'individual' 
                            ? 'Sell as an individual'
                            : 'Sell as a registered business'
                          }
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Business Name
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                  <input
                    type="text"
                    value={form.businessName}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      businessName: e.target.value
                    }))}
                    required
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100 dark:placeholder-gray-500"
                    placeholder="Enter your business name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Business Description
                </label>
                <div className="relative">
                  <FileText className="absolute left-3 top-3 w-5 h-5 text-gray-400 dark:text-gray-500" />
                  <textarea
                    value={form.businessDescription}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      businessDescription: e.target.value
                    }))}
                    required
                    rows={4}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100 dark:placeholder-gray-500"
                    placeholder="Describe your business"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tax ID / EIN
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                  <input
                    type="text"
                    value={form.taxId}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      taxId: e.target.value
                    }))}
                    required={form.businessType === 'company'}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100 dark:placeholder-gray-500"
                    placeholder="Enter your Tax ID or EIN"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Business Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                  <input
                    type="email"
                    value={form.businessEmail}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      businessEmail: e.target.value
                    }))}
                    required
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100 dark:placeholder-gray-500"
                    placeholder="Enter your business email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Business Phone
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                  <input
                    type="tel"
                    value={form.businessPhone}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      businessPhone: e.target.value
                    }))}
                    required
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100 dark:placeholder-gray-500"
                    placeholder="Enter your business phone"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Business Address
                </label>
                <div className="space-y-4">
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                    <input
                      type="text"
                      value={form.address.street}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        address: { ...prev.address, street: e.target.value }
                      }))}
                      required
                      className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100 dark:placeholder-gray-500"
                      placeholder="Street address"
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <input
                      type="text"
                      value={form.address.city}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        address: { ...prev.address, city: e.target.value }
                      }))}
                      required
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100 dark:placeholder-gray-500"
                      placeholder="City"
                    />
                    <input
                      type="text"
                      value={form.address.state}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        address: { ...prev.address, state: e.target.value }
                      }))}
                      required
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100 dark:placeholder-gray-500"
                      placeholder="State"
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <input
                      type="text"
                      value={form.address.postalCode}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        address: { ...prev.address, postalCode: e.target.value }
                      }))}
                      required
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100 dark:placeholder-gray-500"
                      placeholder="Postal code"
                    />
                    <input
                      type="text"
                      value={form.address.country}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        address: { ...prev.address, country: e.target.value }
                      }))}
                      required
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100 dark:placeholder-gray-500"
                      placeholder="Country"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Website (Optional)
                </label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                  <input
                    type="url"
                    value={form.website}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      website: e.target.value
                    }))}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100 dark:placeholder-gray-500"
                    placeholder="Enter your website URL"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Expected Monthly Sales
                </label>
                <select
                  value={form.expectedSales}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    expectedSales: e.target.value
                  }))}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100"
                >
                  <option value="">Select range</option>
                  <option value="0-1000">$0 - $1,000</option>
                  <option value="1000-5000">$1,000 - $5,000</option>
                  <option value="5000-10000">$5,000 - $10,000</option>
                  <option value="10000-50000">$10,000 - $50,000</option>
                  <option value="50000+">$50,000+</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Previous Selling Experience
                </label>
                <textarea
                  value={form.sellingExperience}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    sellingExperience: e.target.value
                  }))}
                  rows={4}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100 dark:placeholder-gray-500"
                  placeholder="Describe your previous selling experience"
                />
              </div>
            </div>
          )}

          <div className="flex justify-between pt-6 border-t dark:border-dark-border">
            {step > 1 && (
              <button
                type="button"
                onClick={() => setStep(prev => prev - 1)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-elevated rounded-lg transition-colors"
              >
                Back
              </button>
            )}
            {step < 3 ? (
              <button
                type="button"
                onClick={() => setStep(prev => prev + 1)}
                className="px-4 py-2 bg-royal-purple text-white rounded-lg hover:bg-royal-purple/90 transition-colors ml-auto"
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading}
                className={cn(
                  "px-4 py-2 rounded-lg font-medium transition-colors ml-auto",
                  loading
                    ? "bg-gray-300 dark:bg-gray-700 cursor-not-allowed"
                    : "bg-royal-purple text-white hover:bg-royal-purple/90"
                )}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Submitting...</span>
                  </div>
                ) : (
                  'Submit Application'
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}